import { User, UserManager } from 'oidc-client-ts';

import { config } from '@inflo/config';
import { BuiltFilePaths, QueryParamKeys } from '@inflo/types';

import { installAuthSessionRecovery } from './auth-session-recovery';
import { InfloOidcStateStore } from './inflo-oidc-state-store';

const INFLO_SUPER_USER_CLAIM = 'inf_super';

const infloOidcPersistence = new InfloOidcStateStore();

/**
 * Built for the end-session request so Inflo Identity can return to `auth-redirect.tsx` after logout.
 * Must match the IdP's registered post-logout redirect URI exactly (origin + path + `signout_callback`
 * query) — client registration is owned outside this repo. If this string drifts, the IdP falls back
 * to its own "Logged Out" page and never returns here, so we never learn logout completed and cannot
 * close the Office dialog automatically. The user can dismiss that page; the parent still clears
 * the local session in `finally`. The happy path is this callback page notifying the parent.
 */
function buildPostLogoutRedirectUri(): string {
  const url = new URL(BuiltFilePaths.SignInRedirect, window.location.origin);
  url.searchParams.set(QueryParamKeys.SignoutCallback, 'true');
  return url.toString();
}

export class Auth {
  /**
   * Cache the JWT decode result by token reference. Decoding runs on every `useAuthState`
   * update; access tokens change rarely so caching cuts ~99% of base64/JSON work.
   * Holding only the latest token avoids unbounded growth.
   */
  private static lastDecodedToken: string | null = null;
  private static lastDecodedPayload: Record<string, unknown> | null = null;

  static readonly userManager = new UserManager({
    authority: config.AUTH_URL,
    client_id: config.CLIENT_ID,
    userStore: infloOidcPersistence,
    stateStore: infloOidcPersistence,
    redirect_uri: new URL(BuiltFilePaths.SignInRedirect, window.location.origin).toString(), // redirect to auth-redirect component
    // Must stay exactly as buildPostLogoutRedirectUri() produces — registered on Inflo Identity Server.
    // If changed, IdP redirects to its fallback logout page; our app never gets the callback and cannot clean the session.
    post_logout_redirect_uri: buildPostLogoutRedirectUri(),
    silent_redirect_uri: new URL(BuiltFilePaths.SilentRenew, window.location.origin).toString(),
    response_type: 'code',
    scope: 'openid profile email offline_access inflo_apiserver ApiPublic AccountingDataExport',
    loadUserInfo: true,
    monitorSession: false,
    monitorAnonymousSession: true,
    filterProtocolClaims: true,
    revokeTokensOnSignout: true,
    // Renewal is wired in `installAuthSessionRecovery` via `coalescedUserManagerSigninSilent` so the
    // library's built-in SilentRenewService never races duplicate `signinSilent` / userinfo calls.
    automaticSilentRenew: false,
  });

  static get infloUserManager() {
    return this.userManager;
  }

  /**
   * Persists the OIDC user. `userManager.storeUser` only writes storage and does not raise
   * `UserLoaded` — we follow with `getUser(true)` so `react-oidc-context` updates
   * `isAuthenticated` (important for the task pane after an Office dialog login, especially
   * Excel on the web).
   */
  static async storeUser(user: User) {
    await this.userManager.storeUser(user);
    return this.syncAuthenticatedUser();
  }

  /** Reloads the persisted user and raises `UserLoaded` so `react-oidc-context` updates `isAuthenticated`. */
  static async syncAuthenticatedUser() {
    return this.userManager.getUser(true);
  }

  /**
   * Clears local OIDC session data (tokens, protocol state, cached JWT decode). Does not replace
   * IdP end-session; the Office sign-out dialog handles that via `signoutRedirect`.
   */
  static async clearLocalSession() {
    Auth.lastDecodedToken = null;
    Auth.lastDecodedPayload = null;
    await this.userManager.removeUser().catch((err) => {
      console.error('Error removing user during local session clear:', err);
    });
    await this.userManager.clearStaleState().catch((err) => {
      console.error('Error clearing stale OIDC state during local session clear:', err);
    });
  }

  /** @param raiseEvent - When true, `UserLoaded` is raised so the AuthProvider updates (e.g. after another window changed `oidc.user` storage). Default false to avoid side effects on hot call paths. */
  static async getUser(raiseEvent: boolean = false) {
    return this.userManager.getUser(raiseEvent);
  }

  static async checkIfHasLimitedAccess(user?: User) {
    if (!user) {
      user = await Auth.getUser();
    }

    if (!user?.access_token) {
      return false;
    }

    const jwtResponse = Auth.decodeJwtToken(user.access_token);

    if (!jwtResponse) {
      return false;
    }

    return INFLO_SUPER_USER_CLAIM in jwtResponse;
  }

  /**
   * Decodes the JWT payload (header.payload.signature) without verifying the signature —
   * verification is the IdP's job, we only inspect non-secret claims like `inf_super`.
   * Result is memoised by token reference because the caller may invoke this on every
   * auth state update.
   */
  static decodeJwtToken(token: string): Record<string, unknown> | null {
    if (token === Auth.lastDecodedToken) {
      return Auth.lastDecodedPayload;
    }
    try {
      const base64Url = token.split('.')[1];
      if (!base64Url) {
        return null;
      }
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
      const payload = JSON.parse(jsonPayload) as Record<string, unknown>;
      Auth.lastDecodedToken = token;
      Auth.lastDecodedPayload = payload;
      return payload;
    } catch (error) {
      console.error('Invalid token', error);
      Auth.lastDecodedToken = token;
      Auth.lastDecodedPayload = null;
      return null;
    }
  }
}

installAuthSessionRecovery(Auth.userManager);
